/**
 * Quick Action Buttons
 * Context-aware action buttons based on workflow status and user role
 */

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { WORKFLOW_STATUS } from '../../utils/constants';
import { hasDirectorAccess } from '../../utils/rolePermissions';
import api from '../../services/api';
import { toast } from 'react-toastify';
import UploadFilesModal from './UploadFilesModal';
import DesignApprovalModal from './DesignApprovalModal';
import RequestAmendmentModal from './RequestAmendmentModal';
import FinalizeQuotationModal from './FinalizeQuotationModal';
import ScheduleSigningModal from './ScheduleSigningModal';
import RecordOutcomeModal from './RecordOutcomeModal';
import MeasurementSubmitModal from './MeasurementSubmitModal';
import Drawing2DSubmitModal from './Drawing2DSubmitModal';
import Drawing3DSubmitModal from './Drawing3DSubmitModal';
import DDSubmitModal from './DDSubmitModal';
import WorkflowAssignmentModal from './WorkflowAssignmentModal';
import CancelProjectModal from './CancelProjectModal';
import RevertStatusModal from './RevertStatusModal';
import BottomSheet from '../common/BottomSheet';

// Priority order for sorting actions
const PRIORITY_ORDER = { primary: 0, secondary: 1, tertiary: 2 };

// Category definitions for grouping actions
const ACTION_CATEGORIES = {
  workflow: {
    label: 'Workflow',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    )
  },
  review: {
    label: 'Review & Approval',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    )
  },
  documents: {
    label: 'Documents',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    )
  },
  scheduling: {
    label: 'Scheduling',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    )
  },
  team: {
    label: 'Team',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    )
  },
  outcome: {
    label: 'Outcome',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
      </svg>
    )
  }
};

// Categorize action based on label patterns
const categorizeAction = (action) => {
  const label = action.label.toLowerCase();

  if (label.includes('start') || label.includes('begin')) return 'workflow';
  if (label.includes('approve') || label.includes('amendment') || label.includes('submit') || label.includes('complete')) return 'review';
  if (label.includes('upload') || label.includes('finalize') || label.includes('contract')) return 'documents';
  if (label.includes('schedule') || label.includes('view measurement')) return 'scheduling';
  if (label.includes('assign')) return 'team';
  if (label.includes('record') || label.includes('outcome') || label.includes('ready') || label.includes('revise')) return 'outcome';

  return 'workflow'; // default
};

// Get softer background color classes for action icons in bottom sheet
const getIconBgClasses = (color) => {
  const colorMap = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    orange: 'bg-orange-100 text-orange-600',
    purple: 'bg-purple-100 text-purple-600',
    indigo: 'bg-indigo-100 text-indigo-600',
    teal: 'bg-teal-100 text-teal-600',
    red: 'bg-red-100 text-red-600'
  };
  return colorMap[color] || colorMap.blue;
};

const QuickActionButtons = ({ project, userRole, onActionComplete, isMobileFAB = false }) => {
  const navigate = useNavigate();

  // Bottom sheet state for mobile
  const [bottomSheetOpen, setBottomSheetOpen] = useState(false);
  const [showMoreActions, setShowMoreActions] = useState(false);

  // Modal states
  const [uploadFilesOpen, setUploadFilesOpen] = useState(false);
  const [designApprovalOpen, setDesignApprovalOpen] = useState(false);
  const [designApprovalType, setDesignApprovalType] = useState(null);
  const [amendmentOpen, setAmendmentOpen] = useState(false);
  const [amendmentType, setAmendmentType] = useState(null);
  const [finalizeQuotationOpen, setFinalizeQuotationOpen] = useState(false);
  const [scheduleSigningOpen, setScheduleSigningOpen] = useState(false);
  const [recordOutcomeOpen, setRecordOutcomeOpen] = useState(false);
  const [measurementSubmitOpen, setMeasurementSubmitOpen] = useState(false);
  const [drawing2DSubmitOpen, setDrawing2DSubmitOpen] = useState(false);
  const [drawing3DSubmitOpen, setDrawing3DSubmitOpen] = useState(false);
  const [ddSubmitOpen, setDDSubmitOpen] = useState(false);
  const [workflowAssignOpen, setWorkflowAssignOpen] = useState(false);
  const [workflowAssignType, setWorkflowAssignType] = useState(null); // 'draughter_2d' or 'designer_3d'
  const [cancelProjectOpen, setCancelProjectOpen] = useState(false);
  const [revertStatusOpen, setRevertStatusOpen] = useState(false);

  const { workflowStatus, id: projectId, currentPackagePrice } = project;

  // Define actions for each status and role
  const getAvailableActions = () => {
    const actions = [];

    // Helper function to update workflow status
    const updateStatus = async (newStatus, statusLabel) => {
      try {
        await api.patch(`/design-projects/${projectId}/workflow-status`, {
          newStatus
        });

        toast.success(`${statusLabel} - Status updated successfully`);

        if (onActionComplete) {
          onActionComplete('status_update', { newStatus, statusLabel });
        }
      } catch (error) {
        console.error('Failed to update status:', error);
        toast.error(`Failed to update status: ${error.message}`);
      }
    };

    // ACTIVE PROJECT - View/Set measurement schedule (measurement auto-created on verification)
    if (workflowStatus === WORKFLOW_STATUS.ACTIVE_PROJECT) {
      actions.push({
        label: 'Set Measurement Schedule',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        ),
        onClick: () => navigate('/dashboard?tab=measurements'),
        color: 'orange',
        priority: 'primary',
        roles: ['manager', 'leader', 'director', 'sid', 'asid']
      });
    }

    // MEASUREMENT PENDING - View measurement schedule & Start measurement
    if (workflowStatus === WORKFLOW_STATUS.MEASUREMENT_PENDING) {
      actions.push({
        label: 'View Measurement Schedule',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        ),
        onClick: () => navigate('/dashboard?tab=measurements'),
        color: 'blue',
        priority: 'secondary',
        roles: ['manager', 'leader', 'director', 'sid', 'asid']
      });

      actions.push({
        label: 'Start Measurement',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        ),
        onClick: () => updateStatus(WORKFLOW_STATUS.MEASUREMENT_IN_PROGRESS, 'Start Measurement'),
        color: 'green',
        priority: 'primary',
        roles: ['manager', 'leader', 'director', 'sid', 'asid']
      });
    }

    // MEASUREMENT IN PROGRESS - Complete & upload
    if (workflowStatus === WORKFLOW_STATUS.MEASUREMENT_IN_PROGRESS) {
      actions.push({
        label: 'Upload Files',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
        ),
        onClick: () => setUploadFilesOpen(true),
        color: 'blue',
        priority: 'secondary',
        roles: ['manager', 'leader', 'director', 'sid', 'asid']
      });

      actions.push({
        label: 'Complete Measurement',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        ),
        onClick: () => setMeasurementSubmitOpen(true),
        color: 'green',
        priority: 'primary',
        roles: ['manager', 'leader', 'director', 'sid', 'asid', 'agent']
      });
    }

    // MEASUREMENT DONE - Assign 2D draughter and move to pending
    if (workflowStatus === WORKFLOW_STATUS.MEASUREMENT_DONE) {
      actions.push({
        label: 'Assign to 2D',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        ),
        onClick: () => {
          setWorkflowAssignType('draughter_2d');
          setWorkflowAssignOpen(true);
        },
        color: 'blue',
        priority: 'primary',
        roles: ['manager', 'leader', 'director', 'sid', 'asid']
      });
    }

    // 2D PENDING - Start 2D work
    if (workflowStatus === WORKFLOW_STATUS.TWO_D_PENDING) {
      actions.push({
        label: 'Start 2D Drawing',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
        ),
        onClick: () => updateStatus(WORKFLOW_STATUS.TWO_D_IN_PROGRESS, 'Start 2D Drawing'),
        color: 'purple',
        priority: 'primary',
        roles: ['manager', 'leader', 'director', 'sid', 'asid']
      });
    }

    // 2D IN PROGRESS - Upload & submit for review
    if (workflowStatus === WORKFLOW_STATUS.TWO_D_IN_PROGRESS) {
      actions.push({
        label: 'Upload Files',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
        ),
        onClick: () => setUploadFilesOpen(true),
        color: 'blue',
        priority: 'secondary',
        roles: ['manager', 'leader', 'director', 'sid', 'asid']
      });

      actions.push({
        label: 'Submit for Review',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        ),
        onClick: () => setDrawing2DSubmitOpen(true),
        color: 'green',
        priority: 'primary',
        roles: ['manager', 'leader', 'director', 'sid', 'asid']
      });
    }

    // 3D IN PROGRESS - Upload & submit
    if (workflowStatus === WORKFLOW_STATUS.THREE_D_IN_PROGRESS) {
      actions.push({
        label: 'Upload Files',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
        ),
        onClick: () => setUploadFilesOpen(true),
        color: 'blue',
        priority: 'secondary',
        roles: ['manager', 'leader', 'director', 'sid', 'asid']
      });

      actions.push({
        label: 'Submit for Review',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        ),
        onClick: () => setDrawing3DSubmitOpen(true),
        color: 'green',
        priority: 'primary',
        roles: ['manager', 'leader', 'director', 'sid', 'asid']
      });
    }

    // QUOTATION IN PROGRESS - Upload files
    if (workflowStatus === WORKFLOW_STATUS.QUOTATION_IN_PROGRESS) {
      actions.push({
        label: 'Upload Files',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
        ),
        onClick: () => setUploadFilesOpen(true),
        color: 'blue',
        priority: 'secondary',
        roles: ['manager', 'leader', 'director', 'sid', 'asid']
      });
    }

    // 2D Design Actions
    if (workflowStatus === WORKFLOW_STATUS.TWO_D_PENDING_CLIENT_REVIEW) {
      actions.push({
        label: 'Approve 2D Design',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        ),
        onClick: () => {
          setDesignApprovalType('2d');
          setDesignApprovalOpen(true);
        },
        color: 'green',
        priority: 'primary',
        roles: ['manager', 'leader', 'director', 'sid', 'asid']
      });

      actions.push({
        label: 'Request 2D Amendment',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        ),
        onClick: () => {
          setAmendmentType('2d');
          setAmendmentOpen(true);
        },
        color: 'orange',
        priority: 'secondary',
        roles: ['manager', 'leader', 'director', 'sid', 'asid']
      });
    }

    // 2D AMENDMENT REQUIRED - Start working on amendments
    if (workflowStatus === WORKFLOW_STATUS.TWO_D_AMENDMENT_REQUIRED) {
      actions.push({
        label: 'Start 2D Amendment',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
        ),
        onClick: () => updateStatus(WORKFLOW_STATUS.TWO_D_IN_PROGRESS, 'Start 2D Amendment'),
        color: 'orange',
        priority: 'primary',
        roles: ['manager', 'leader', 'director', 'sid', 'asid']
      });
    }

    // 2D APPROVED - Assign 3D designer and move to pending
    if (workflowStatus === WORKFLOW_STATUS.TWO_D_APPROVED) {
      actions.push({
        label: 'Assign to 3D',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
          </svg>
        ),
        onClick: () => {
          setWorkflowAssignType('designer_3d');
          setWorkflowAssignOpen(true);
        },
        color: 'purple',
        priority: 'primary',
        roles: ['manager', 'leader', 'director', 'sid', 'asid']
      });
    }

    // 3D PENDING - Start 3D work
    if (workflowStatus === WORKFLOW_STATUS.THREE_D_PENDING) {
      actions.push({
        label: 'Start 3D Design',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        ),
        onClick: () => updateStatus(WORKFLOW_STATUS.THREE_D_IN_PROGRESS, 'Start 3D Design'),
        color: 'purple',
        priority: 'primary',
        roles: ['manager', 'leader', 'director', 'sid', 'asid']
      });
    }

    // 3D Design Actions
    if (workflowStatus === WORKFLOW_STATUS.THREE_D_PENDING_CLIENT_REVIEW) {
      actions.push({
        label: 'Approve 3D Design',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        ),
        onClick: () => {
          setDesignApprovalType('3d');
          setDesignApprovalOpen(true);
        },
        color: 'green',
        priority: 'primary',
        roles: ['manager', 'leader', 'director', 'sid', 'asid']
      });

      actions.push({
        label: 'Request 3D Amendment',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        ),
        onClick: () => {
          setAmendmentType('3d');
          setAmendmentOpen(true);
        },
        color: 'orange',
        priority: 'secondary',
        roles: ['manager', 'leader', 'director', 'sid', 'asid']
      });
    }

    // 3D AMENDMENT REQUIRED - Start working on amendments
    if (workflowStatus === WORKFLOW_STATUS.THREE_D_AMENDMENT_REQUIRED) {
      actions.push({
        label: 'Start 3D Amendment',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        ),
        onClick: () => updateStatus(WORKFLOW_STATUS.THREE_D_IN_PROGRESS, 'Start 3D Amendment'),
        color: 'orange',
        priority: 'primary',
        roles: ['manager', 'leader', 'director', 'sid', 'asid']
      });
    }

    // 3D APPROVED - Start DD (Detailed Design)
    if (workflowStatus === WORKFLOW_STATUS.THREE_D_APPROVED) {
      actions.push({
        label: 'Start DD Phase',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
        ),
        onClick: () => updateStatus(WORKFLOW_STATUS.DD_PENDING, 'Start DD Phase'),
        color: 'blue',
        priority: 'primary',
        roles: ['manager', 'leader', 'director', 'sid', 'asid']
      });
    }

    // DD PENDING - Start DD Work
    if (workflowStatus === WORKFLOW_STATUS.DD_PENDING) {
      actions.push({
        label: 'Start DD Work',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
        ),
        onClick: () => updateStatus(WORKFLOW_STATUS.DD_IN_PROGRESS, 'Start DD Work'),
        color: 'purple',
        priority: 'primary',
        roles: ['manager', 'leader', 'director', 'sid', 'asid']
      });
    }

    // DD IN PROGRESS - Upload files & Submit to Quotation
    if (workflowStatus === WORKFLOW_STATUS.DD_IN_PROGRESS) {
      actions.push({
        label: 'Upload Files',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
        ),
        onClick: () => setUploadFilesOpen(true),
        color: 'blue',
        priority: 'secondary',
        roles: ['manager', 'leader', 'director', 'sid', 'asid']
      });

      actions.push({
        label: 'Submit to Quotation',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        ),
        onClick: () => setDDSubmitOpen(true),
        color: 'green',
        priority: 'primary',
        roles: ['manager', 'leader', 'director', 'sid', 'asid']
      });
    }

    // DD SUBMITTED TO QUOTATION - Start Quotation Work
    if (workflowStatus === WORKFLOW_STATUS.DD_SUBMITTED_TO_QUOTATION) {
      actions.push({
        label: 'Start Quotation',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        ),
        onClick: () => updateStatus(WORKFLOW_STATUS.QUOTATION_IN_PROGRESS, 'Start Quotation'),
        color: 'purple',
        priority: 'primary',
        roles: ['manager', 'leader', 'director', 'sid', 'asid']
      });
    }

    // SIGNING AMENDMENT REQUIRED - Client negotiation
    if (workflowStatus === WORKFLOW_STATUS.SIGNING_AMENDMENT_REQUIRED) {
      actions.push({
        label: 'Revise DD',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        ),
        onClick: () => updateStatus(WORKFLOW_STATUS.DD_IN_PROGRESS, 'Revise DD for Client'),
        color: 'orange',
        priority: 'secondary',
        roles: ['manager', 'leader', 'director', 'sid', 'asid']
      });

      actions.push({
        label: 'Ready for Signing',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        ),
        onClick: () => updateStatus(WORKFLOW_STATUS.READY_FOR_SIGNING, 'Ready for Signing'),
        color: 'green',
        priority: 'primary',
        roles: ['manager', 'leader', 'director', 'sid', 'asid']
      });

      actions.push({
        label: 'Record Outcome',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
        ),
        onClick: () => setRecordOutcomeOpen(true),
        color: 'blue',
        priority: 'tertiary',
        roles: ['manager', 'leader', 'director', 'sid', 'asid']
      });
    }

    // Quotation Actions
    if (workflowStatus === WORKFLOW_STATUS.QUOTATION_IN_PROGRESS) {
      actions.push({
        label: 'Finalize Quotation',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        ),
        onClick: () => setFinalizeQuotationOpen(true),
        color: 'purple',
        priority: 'primary',
        roles: ['manager', 'leader', 'director', 'sid', 'asid']
      });
    }

    // Contract Actions
    if (workflowStatus === WORKFLOW_STATUS.READY_FOR_SIGNING) {
      actions.push({
        label: 'Upload Contract',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
          </svg>
        ),
        onClick: () => setUploadFilesOpen(true),
        color: 'indigo',
        priority: 'secondary',
        roles: ['manager', 'leader', 'director', 'sid', 'asid']
      });

      actions.push({
        label: 'Schedule Signing',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        ),
        onClick: () => setScheduleSigningOpen(true),
        color: 'teal',
        priority: 'primary',
        roles: ['manager', 'leader', 'director', 'sid', 'asid']
      });
    }

    // Signing Scheduled - Record Outcome
    if (workflowStatus === WORKFLOW_STATUS.SIGNING_SCHEDULED) {
      actions.push({
        label: 'Record Outcome',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        ),
        onClick: () => setRecordOutcomeOpen(true),
        color: 'green',
        priority: 'primary',
        roles: ['manager', 'leader', 'director', 'sid', 'asid']
      });
    }

    // ========================================
    // CANCEL & REVERT ACTIONS
    // Available for manager/director at most statuses
    // ========================================

    // Terminal statuses where cancel/revert are not applicable
    const terminalStatuses = [
      WORKFLOW_STATUS.PROJECT_CLOSED_WON,
      WORKFLOW_STATUS.PROJECT_CLOSED_LOST,
      WORKFLOW_STATUS.PROJECT_CANCELLED
    ];

    // Cancel Project - available for all non-terminal statuses
    if (!terminalStatuses.includes(workflowStatus)) {
      actions.push({
        label: 'Cancel Project',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        ),
        onClick: () => setCancelProjectOpen(true),
        color: 'red',
        priority: 'tertiary',
        roles: ['manager', 'director'],
        hideFromQuickGrid: true // Always show in "More Actions" on mobile
      });
    }

    // Revert Status - available for all statuses except project_closed_won
    // project_closed_lost and project_cancelled CAN be reverted
    if (workflowStatus !== WORKFLOW_STATUS.PROJECT_CLOSED_WON) {
      actions.push({
        label: 'Revert Status',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
          </svg>
        ),
        onClick: () => setRevertStatusOpen(true),
        color: 'orange',
        priority: 'tertiary',
        roles: ['manager', 'director'],
        hideFromQuickGrid: true // Always show in "More Actions" on mobile
      });
    }

    return actions;
  };

  const availableActions = getAvailableActions();

  // Filter by role - check for director equivalents when 'director' is in roles
  const filteredActions = availableActions.filter(action => {
    if (action.roles.includes('all')) return true;
    if (action.roles.includes(userRole?.toLowerCase())) return true;
    // If action allows 'director', also allow director-equivalent roles
    if (action.roles.includes('director') && hasDirectorAccess(userRole)) return true;
    return false;
  });

  // Color classes
  const getColorClasses = (color) => {
    const colorMap = {
      blue: 'bg-blue-600 hover:bg-blue-700 text-white',
      green: 'bg-green-600 hover:bg-green-700 text-white',
      orange: 'bg-orange-600 hover:bg-orange-700 text-white',
      purple: 'bg-purple-600 hover:bg-purple-700 text-white',
      indigo: 'bg-indigo-600 hover:bg-indigo-700 text-white',
      teal: 'bg-teal-600 hover:bg-teal-700 text-white',
      red: 'bg-red-600 hover:bg-red-700 text-white'
    };
    return colorMap[color] || colorMap.blue;
  };

  // Handle action completion
  const handleActionComplete = (actionType, data) => {
    if (onActionComplete) {
      onActionComplete(actionType, data);
    }
  };

  // Group actions by category for mobile bottom sheet
  const groupedActions = useMemo(() => {
    const groups = {};
    filteredActions.forEach(action => {
      const category = categorizeAction(action);
      if (!groups[category]) groups[category] = [];
      groups[category].push(action);
    });
    return groups;
  }, [filteredActions]);

  // Sort actions by priority and split into quick (top 3) and more actions
  // Actions with hideFromQuickGrid always go to moreActions
  const { quickActions, moreActions } = useMemo(() => {
    const sorted = [...filteredActions].sort(
      (a, b) => (PRIORITY_ORDER[a.priority] || 1) - (PRIORITY_ORDER[b.priority] || 1)
    );
    // Separate actions that must be hidden from quick grid
    const gridEligible = sorted.filter(a => !a.hideFromQuickGrid);
    const alwaysMore = sorted.filter(a => a.hideFromQuickGrid);

    return {
      quickActions: gridEligible.slice(0, 3),
      moreActions: [...gridEligible.slice(3), ...alwaysMore]
    };
  }, [filteredActions]);

  // Determine contextual primary action for mobile FAB
  const getPrimaryFABAction = () => {
    // Return top priority action if available
    if (quickActions.length > 0) {
      return quickActions[0];
    }
    return null;
  };

  const primaryFABAction = isMobileFAB ? getPrimaryFABAction() : null;

  // Get FAB background color
  const getFABBgColor = (color) => {
    const colorMap = {
      blue: 'bg-blue-600 hover:bg-blue-700',
      green: 'bg-green-600 hover:bg-green-700',
      yellow: 'bg-yellow-500 hover:bg-yellow-600',
      orange: 'bg-orange-600 hover:bg-orange-700',
      purple: 'bg-purple-600 hover:bg-purple-700',
      red: 'bg-red-600 hover:bg-red-700',
      teal: 'bg-teal-600 hover:bg-teal-700',
      indigo: 'bg-indigo-600 hover:bg-indigo-700',
      gray: 'bg-gray-600 hover:bg-gray-700'
    };
    return colorMap[color] || colorMap.blue;
  };

  // Get priority classes for desktop toolbar
  const getPriorityClasses = (priority) => {
    const priorityMap = {
      primary: 'ring-2 ring-offset-1 ring-current/20 shadow-md',
      secondary: 'shadow-sm',
      tertiary: 'opacity-80 text-xs'
    };
    return priorityMap[priority] || priorityMap.secondary;
  };

  // Define category display order
  const categoryOrder = ['workflow', 'review', 'documents', 'scheduling', 'team', 'outcome'];

  if (filteredActions.length === 0) {
    return isMobileFAB ? null : null;
  }

  // Mobile FAB render
  if (isMobileFAB) {
    return (
      <>
        {/* Mobile: Single Lightning FAB that opens bottom sheet */}
        <div className="fixed bottom-6 right-6 z-40">
          <button
            onClick={() => setBottomSheetOpen(true)}
            className="w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-200 touch-manipulation active:scale-95 bg-blue-600 hover:bg-blue-700"
            title="Quick Actions"
          >
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </button>
        </div>

        {/* Bottom Sheet with quick access grid + more actions */}
        <BottomSheet
          isOpen={bottomSheetOpen}
          onClose={() => {
            setBottomSheetOpen(false);
            setShowMoreActions(false);
          }}
          title="Quick Actions"
        >
          <div className="pb-4">
            {/* Quick Access Grid - Top 3 priority actions */}
            {quickActions.length > 0 && (
              <div className="px-4 py-3">
                <div className="grid grid-cols-3 gap-3">
                  {quickActions.map((action, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        action.onClick();
                        setBottomSheetOpen(false);
                      }}
                      className="flex flex-col items-center gap-2 p-3 rounded-xl bg-gray-50 active:bg-gray-100 transition-colors touch-manipulation"
                    >
                      <span className={`w-12 h-12 rounded-full flex items-center justify-center ${getIconBgClasses(action.color)}`}>
                        <span className="w-6 h-6 [&>svg]:w-6 [&>svg]:h-6">{action.icon}</span>
                      </span>
                      <span className="text-xs font-medium text-gray-700 text-center leading-tight line-clamp-2">
                        {action.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* More Actions - Collapsible section */}
            {moreActions.length > 0 && (
              <div className="border-t border-gray-100">
                <button
                  onClick={() => setShowMoreActions(!showMoreActions)}
                  className="w-full flex items-center justify-between px-4 py-3 text-left active:bg-gray-50 touch-manipulation"
                >
                  <span className="text-sm font-medium text-gray-600">
                    More Actions ({moreActions.length})
                  </span>
                  <svg
                    className={`w-5 h-5 text-gray-400 transition-transform ${showMoreActions ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Collapsible content */}
                <div className={`overflow-hidden transition-all duration-200 ${showMoreActions ? 'max-h-96' : 'max-h-0'}`}>
                  {moreActions.map((action, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        action.onClick();
                        setBottomSheetOpen(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left transition-colors active:bg-gray-50 touch-manipulation min-h-[52px] border-t border-gray-50"
                    >
                      <span className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${getIconBgClasses(action.color)}`}>
                        <span className="w-4 h-4 [&>svg]:w-4 [&>svg]:h-4">{action.icon}</span>
                      </span>
                      <span className="text-sm text-gray-700">{action.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </BottomSheet>

        {/* Modals */}
        <UploadFilesModal
          isOpen={uploadFilesOpen}
          onClose={() => setUploadFilesOpen(false)}
          projectId={projectId}
          workflowStatus={workflowStatus}
          onSuccess={(data) => {
            handleActionComplete('upload_files', data);
            setUploadFilesOpen(false);
          }}
        />

        <DesignApprovalModal
          isOpen={designApprovalOpen}
          onClose={() => {
            setDesignApprovalOpen(false);
            setDesignApprovalType(null);
          }}
          projectId={projectId}
          designType={designApprovalType}
          currentPrice={currentPackagePrice}
          onSuccess={(data) => {
            handleActionComplete('design_approval', data);
            setDesignApprovalOpen(false);
            setDesignApprovalType(null);
          }}
        />

        <RequestAmendmentModal
          isOpen={amendmentOpen}
          onClose={() => {
            setAmendmentOpen(false);
            setAmendmentType(null);
          }}
          projectId={projectId}
          designType={amendmentType}
          onSuccess={(data) => {
            handleActionComplete('request_amendment', data);
            setAmendmentOpen(false);
            setAmendmentType(null);
          }}
        />

        <FinalizeQuotationModal
          isOpen={finalizeQuotationOpen}
          onClose={() => setFinalizeQuotationOpen(false)}
          projectId={projectId}
          currentPrice={currentPackagePrice}
          onSuccess={(data) => {
            handleActionComplete('finalize_quotation', data);
            setFinalizeQuotationOpen(false);
          }}
        />

        <ScheduleSigningModal
          isOpen={scheduleSigningOpen}
          onClose={() => setScheduleSigningOpen(false)}
          projectId={projectId}
          onSuccess={(data) => {
            handleActionComplete('schedule_signing', data);
            setScheduleSigningOpen(false);
          }}
        />

        <RecordOutcomeModal
          isOpen={recordOutcomeOpen}
          onClose={() => setRecordOutcomeOpen(false)}
          projectId={projectId}
          onSuccess={(data) => {
            handleActionComplete('record_outcome', data);
            setRecordOutcomeOpen(false);
          }}
        />

        <MeasurementSubmitModal
          isOpen={measurementSubmitOpen}
          onClose={() => setMeasurementSubmitOpen(false)}
          projectId={projectId}
          onSuccess={(data) => {
            handleActionComplete('measurement_submit', data);
            setMeasurementSubmitOpen(false);
          }}
        />

        <Drawing2DSubmitModal
          isOpen={drawing2DSubmitOpen}
          onClose={() => setDrawing2DSubmitOpen(false)}
          projectId={projectId}
          onSuccess={(data) => {
            handleActionComplete('drawing_2d_submit', data);
            setDrawing2DSubmitOpen(false);
          }}
        />

        <Drawing3DSubmitModal
          isOpen={drawing3DSubmitOpen}
          onClose={() => setDrawing3DSubmitOpen(false)}
          projectId={projectId}
          onSuccess={(data) => {
            handleActionComplete('drawing_3d_submit', data);
            setDrawing3DSubmitOpen(false);
          }}
        />

        <DDSubmitModal
          isOpen={ddSubmitOpen}
          onClose={() => setDDSubmitOpen(false)}
          projectId={projectId}
          onSuccess={(data) => {
            handleActionComplete('dd_submit', data);
            setDDSubmitOpen(false);
          }}
        />

        <WorkflowAssignmentModal
          isOpen={workflowAssignOpen}
          onClose={() => {
            setWorkflowAssignOpen(false);
            setWorkflowAssignType(null);
          }}
          projectId={projectId}
          roleType={workflowAssignType}
          targetStatus={workflowAssignType === 'draughter_2d'
            ? WORKFLOW_STATUS.TWO_D_PENDING
            : WORKFLOW_STATUS.THREE_D_PENDING}
          currentTeamMembers={project?.teamMembers || []}
          transitionWorkflow={true}
          onSuccess={(data) => {
            handleActionComplete('workflow_assign', data);
            setWorkflowAssignOpen(false);
            setWorkflowAssignType(null);
          }}
        />

        <CancelProjectModal
          isOpen={cancelProjectOpen}
          onClose={() => setCancelProjectOpen(false)}
          projectId={projectId}
          projectNumber={project?.projectNumber}
          onSuccess={(data) => {
            handleActionComplete('project_cancelled', data);
            setCancelProjectOpen(false);
          }}
        />

        <RevertStatusModal
          isOpen={revertStatusOpen}
          onClose={() => setRevertStatusOpen(false)}
          projectId={projectId}
          projectNumber={project?.projectNumber}
          currentStatus={workflowStatus}
          onSuccess={(data) => {
            handleActionComplete('status_reverted', data);
            setRevertStatusOpen(false);
          }}
        />
      </>
    );
  }

  // Desktop: Horizontal row of colored buttons (matching SiteDetailPage style)
  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        {filteredActions
          .sort((a, b) => (PRIORITY_ORDER[a.priority] || 1) - (PRIORITY_ORDER[b.priority] || 1))
          .map((action, index) => (
          <button
            key={index}
            onClick={action.onClick}
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg font-medium transition-all text-sm shadow-sm hover:shadow-md active:scale-[0.98] min-h-[40px] touch-manipulation ${getColorClasses(action.color)}`}
            title={action.label}
          >
            <span className="w-4 h-4 [&>svg]:w-4 [&>svg]:h-4">{action.icon}</span>
            <span>{action.label}</span>
          </button>
        ))}
      </div>

      {/* Modals */}
      <UploadFilesModal
        isOpen={uploadFilesOpen}
        onClose={() => setUploadFilesOpen(false)}
        projectId={projectId}
        workflowStatus={workflowStatus}
        onSuccess={(data) => {
          handleActionComplete('upload_files', data);
          setUploadFilesOpen(false);
        }}
      />

      <DesignApprovalModal
        isOpen={designApprovalOpen}
        onClose={() => {
          setDesignApprovalOpen(false);
          setDesignApprovalType(null);
        }}
        projectId={projectId}
        designType={designApprovalType}
        currentPrice={currentPackagePrice}
        onSuccess={(data) => {
          handleActionComplete('design_approval', data);
          setDesignApprovalOpen(false);
          setDesignApprovalType(null);
        }}
      />

      <RequestAmendmentModal
        isOpen={amendmentOpen}
        onClose={() => {
          setAmendmentOpen(false);
          setAmendmentType(null);
        }}
        projectId={projectId}
        designType={amendmentType}
        onSuccess={(data) => {
          handleActionComplete('request_amendment', data);
          setAmendmentOpen(false);
          setAmendmentType(null);
        }}
      />

      <FinalizeQuotationModal
        isOpen={finalizeQuotationOpen}
        onClose={() => setFinalizeQuotationOpen(false)}
        projectId={projectId}
        currentPrice={currentPackagePrice}
        onSuccess={(data) => {
          handleActionComplete('finalize_quotation', data);
          setFinalizeQuotationOpen(false);
        }}
      />

      <ScheduleSigningModal
        isOpen={scheduleSigningOpen}
        onClose={() => setScheduleSigningOpen(false)}
        projectId={projectId}
        onSuccess={(data) => {
          handleActionComplete('schedule_signing', data);
          setScheduleSigningOpen(false);
        }}
      />

      <RecordOutcomeModal
        isOpen={recordOutcomeOpen}
        onClose={() => setRecordOutcomeOpen(false)}
        projectId={projectId}
        onSuccess={(data) => {
          handleActionComplete('record_outcome', data);
          setRecordOutcomeOpen(false);
        }}
      />

      <MeasurementSubmitModal
        isOpen={measurementSubmitOpen}
        onClose={() => setMeasurementSubmitOpen(false)}
        projectId={projectId}
        onSuccess={(data) => {
          handleActionComplete('measurement_submit', data);
          setMeasurementSubmitOpen(false);
        }}
      />

      <Drawing2DSubmitModal
        isOpen={drawing2DSubmitOpen}
        onClose={() => setDrawing2DSubmitOpen(false)}
        projectId={projectId}
        onSuccess={(data) => {
          handleActionComplete('drawing_2d_submit', data);
          setDrawing2DSubmitOpen(false);
        }}
      />

      <Drawing3DSubmitModal
        isOpen={drawing3DSubmitOpen}
        onClose={() => setDrawing3DSubmitOpen(false)}
        projectId={projectId}
        onSuccess={(data) => {
          handleActionComplete('drawing_3d_submit', data);
          setDrawing3DSubmitOpen(false);
        }}
      />

      <DDSubmitModal
        isOpen={ddSubmitOpen}
        onClose={() => setDDSubmitOpen(false)}
        projectId={projectId}
        onSuccess={(data) => {
          handleActionComplete('dd_submit', data);
          setDDSubmitOpen(false);
        }}
      />

      <WorkflowAssignmentModal
        isOpen={workflowAssignOpen}
        onClose={() => {
          setWorkflowAssignOpen(false);
          setWorkflowAssignType(null);
        }}
        projectId={projectId}
        roleType={workflowAssignType}
        targetStatus={workflowAssignType === 'draughter_2d'
          ? WORKFLOW_STATUS.TWO_D_PENDING
          : WORKFLOW_STATUS.THREE_D_PENDING}
        currentTeamMembers={project?.teamMembers || []}
        transitionWorkflow={true}
        onSuccess={(data) => {
          handleActionComplete('workflow_assign', data);
          setWorkflowAssignOpen(false);
          setWorkflowAssignType(null);
        }}
      />

      <CancelProjectModal
        isOpen={cancelProjectOpen}
        onClose={() => setCancelProjectOpen(false)}
        projectId={projectId}
        projectNumber={project?.projectNumber}
        onSuccess={(data) => {
          handleActionComplete('project_cancelled', data);
          setCancelProjectOpen(false);
        }}
      />

      <RevertStatusModal
        isOpen={revertStatusOpen}
        onClose={() => setRevertStatusOpen(false)}
        projectId={projectId}
        projectNumber={project?.projectNumber}
        currentStatus={workflowStatus}
        onSuccess={(data) => {
          handleActionComplete('status_reverted', data);
          setRevertStatusOpen(false);
        }}
      />
    </>
  );
};

export default QuickActionButtons;
